import Image from 'next/legacy/image';
import { FC, useState } from 'react';
import ReactTooltip from 'react-tooltip';

import { bulkDiscountText, LineItemProps, Styled } from './';

import { Dropdown } from 'components/core';
import {
  useRemoveLineItem,
  useUpdateLineItemQuantity,
} from 'reactQuery/mutations';
import { IconWrapper, LoadingSpinner } from 'styles/sharedStyles';
import Link from 'next/link';
import { useI18n } from 'contexts/i18n';
import {
  filterBulkDiscountBySku,
  filterDSODiscountBySku,
} from 'components/ProductTile/ProductTile.helper';

import { formatDateToMonthYear, isEMEAStore } from 'utils';
import LineItemPreviewModal from './LineItemPreviewModal';
import LineItemDiscounts from './LineItemDiscounts';
import WarningModal from 'components/WarningModal/WarningModal';

export const LineItem: FC<LineItemProps> = ({
  lineItem,
  isCustomClamShellOrder,
  promotionList,
  isRewardPointsAvailable,
  stockError,
  setStockError,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const removeLineItemMutation = useRemoveLineItem();
  const updateQuantityMutation = useUpdateLineItemQuantity();
  const [quantity, setQuantity] = useState(lineItem.quantity.toString());
  const i18n = useI18n();
  const mutationError = updateQuantityMutation?.error?.response?.data?.error;
  const errorMessage = stockError || mutationError;

  const deleteLineItem = (lineItemId: string) => {
    removeLineItemMutation.mutate({ lineItemId });
  };

  const updateLineItemQuantity = (
    lineItemId: string,
    quantity: number,
    isRewardPointSelected: boolean
  ) => {
    updateQuantityMutation.mutate(
      { lineItemId, quantity, isRewardPointSelected },
      {
        onError: error => {
          setQuantity(lineItem.quantity.toString());
        },
        onSuccess: () => {
          setQuantity(quantity.toString());
        },
      }
    );
  };

  const isSubscribedProduct = lineItem.isSubscribed;

  return (
    <>
      <Styled.LineItemContainer
        disabled={!lineItem.inStock || lineItem.isOriginalProductPriceChanged}
        isCustomClamShellOrder={isCustomClamShellOrder}
        isRewardPointsAvailable={isRewardPointsAvailable}
      >
        <Styled.LineItemThumbnail
          isCustomClamShellOrder={isCustomClamShellOrder}
        >
          {isCustomClamShellOrder ? (
            <Image
              src={lineItem.imageUrl}
              alt="Prod Image"
              layout="fill"
              objectFit="cover"
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
            />
          ) : (
            <Link href={`/product/${lineItem.slug}`} passHref>
              {/* this <> </> empty bracket, wrapping around the Image component prevents Next JS error about forward ref. */}

              <Image
                src={lineItem.imageUrl}
                alt="Prod Image"
                layout="fill"
                objectFit="cover"
                placeholder="blur"
                blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
              />
            </Link>
          )}
        </Styled.LineItemThumbnail>
        <Styled.LineItemDescription
          isCustomClamShellOrder={isCustomClamShellOrder}
        >
          {lineItem.name}
          <Styled.LineItemAttributes>
            {isEMEAStore() && (
              <Styled.Availibility inStock={lineItem.inStock}>
                {!lineItem.inStock && i18n['Out of Stock']}
              </Styled.Availibility>
            )}
            {lineItem.isCustomizable && (
              <Styled.Availibility
                inStock={lineItem.inStock}
                isCustomClamShellOrder={isCustomClamShellOrder}
              >
                <Styled.PreviewContainer>
                  <Styled.Link onClick={() => setShowPreview(true)}>
                    i18n['Preview']
                  </Styled.Link>
                </Styled.PreviewContainer>
              </Styled.Availibility>
            )}
          </Styled.LineItemAttributes>

          <LineItemDiscounts lineItem={lineItem} />
          {lineItem.discontinueOn && (
            <Styled.ExpirationDate>
              {i18n['Expires on']}{' '}
              {formatDateToMonthYear(lineItem.discontinueOn)}
            </Styled.ExpirationDate>
          )}
        </Styled.LineItemDescription>
        <Styled.LineItemPriceContainer>
          <Styled.LineItemPrice>{`${
            !isCustomClamShellOrder ? lineItem.displayPrice : ''
          }`}</Styled.LineItemPrice>
        </Styled.LineItemPriceContainer>
        {isRewardPointsAvailable && (
          <Styled.LineItemPriceContainer>
            <Styled.LineItemPrice>
              {parseInt(lineItem.redeemedPoints) > 0
                ? lineItem?.redeemedPoints
                : '0'}
            </Styled.LineItemPrice>
          </Styled.LineItemPriceContainer>
        )}
        <Styled.LineItemQuantity>
          {!isCustomClamShellOrder && !isSubscribedProduct ? (
            <Dropdown
              options={lineItem.quantityLimit}
              onChange={val => {
                setQuantity(val);
                updateLineItemQuantity(
                  lineItem.id,
                  parseInt(val),
                  lineItem.isRewardPointsRedeemed
                );
              }}
              selectedValue={quantity}
              label={i18n['Qty']}
              width={'100%'}
              background={'white'}
              isDisabled={
                !lineItem.inStock || lineItem.isOriginalProductPriceChanged
              }
              size="xs"
              isLoading={updateQuantityMutation.isLoading}
            ></Dropdown>
          ) : (
            lineItem.quantity.toString()
          )}
        </Styled.LineItemQuantity>
        <Styled.LineItemTotal>
          {!isCustomClamShellOrder ? `${lineItem.displayTotal}` : null}

          {lineItem.isHavingAnyAdjustments && (
            <Styled.LineItemTotalWithoutDiscount>
              {!isCustomClamShellOrder
                ? `${lineItem.displayTotalWithoutDiscount}`
                : null}
            </Styled.LineItemTotalWithoutDiscount>
          )}
        </Styled.LineItemTotal>
        {!isCustomClamShellOrder ? (
          <IconWrapper onClick={() => deleteLineItem(lineItem.id)}>
            {!removeLineItemMutation.isLoading && (
              <Image
                src="/images/icons/delete.svg"
                alt="me"
                width="24"
                height="24"
              />
            )}
            {removeLineItemMutation.isLoading && <LoadingSpinner />}
          </IconWrapper>
        ) : null}
        {showPreview && (
          <LineItemPreviewModal
            onClose={() => setShowPreview(false)}
            projectId={lineItem.dhProjectId}
          />
        )}
        <ReactTooltip
          place="bottom"
          className={'tooltip'}
          backgroundColor="rgba(2, 119, 180, 1)"
        />
      </Styled.LineItemContainer>
      {errorMessage && (
        <WarningModal
          errorMessage={errorMessage}
          onClose={() => {
            updateQuantityMutation.reset();
            setStockError('');
          }}
        />
      )}
    </>
  );
};
